"use client";
import { ReactNode } from 'react';
import { useFleetGpsAccess } from '@/hooks/use-fleet-gps-access';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

interface GpsLicenseGateProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function GpsLicenseGate({ children, fallback }: GpsLicenseGateProps) {
  const { effectiveCompanyId } = useAuth();
  const { hasAccess, isLoading, reason } = useFleetGpsAccess(effectiveCompanyId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hasAccess) {
    if (fallback) return <>{fallback}</>;

    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <MapPin className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl">GPS - FleetONE</CardTitle>
            <p className="text-muted-foreground">
              {reason || 'Ative o FleetONE para acessar funcionalidades GPS'}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="font-medium">Rastreamento Ao Vivo</div>
                  <div className="text-muted-foreground">
                    Monitoramento em tempo real da localização dos veículos
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="font-medium">Histórico de Viagens</div>
                  <div className="text-muted-foreground">
                    Replay completo das rotas percorridas
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="font-medium">Alertas & Geocercas</div>
                  <div className="text-muted-foreground">
                    Notificações automáticas e zonas geográficas
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Funcionalidades GPS incluídas no FleetONE
                  </div>
                  <div className="text-base text-muted-foreground">
                    Rastreamento em tempo real, histórico de viagens, geocercas e muito mais
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg">
                  <Link to="/admin/systems">
                    Ativar FleetONE
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/fleet">
                    Voltar à Frota
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}